import type { ReactElement, ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@csa/ui';

/**
 * Shown when the application cannot start at all: configuration failed to
 * load, or MSAL could not initialise. Deliberately depends on nothing but the
 * design tokens, because by definition the usual providers are unavailable.
 */
export function FatalError({
  title,
  message,
  children,
}: {
  title: string;
  message: string;
  children?: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-sunken p-6">
      <div className="w-full max-w-lg rounded-lg border border-line bg-surface p-8 shadow-card">
        <div className="flex items-start gap-4">
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-subtle">
            <ExclamationTriangleIcon className="size-5 text-brand" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-body">{title}</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted">{message}</p>
            {children ? <div className="mt-4">{children}</div> : null}
            <div className="mt-6 flex items-center gap-3">
              <Button onClick={() => window.location.reload()}>Reload</Button>
              <p className="text-xs text-subtle">
                If this persists, contact the service desk and quote the message above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
